import React, { useState, useEffect, useRef } from "react";
import { Calendar, X, ChevronDown, ChevronUp } from "lucide-react";
import { getDefaultDateRange, isPastDaysRange } from "../utils/helpers";

const CustomDateRangePicker = ({
  dateFilterType,
  setDateFilterType,
  singleDate,
  setSingleDate,
  dateRangeStart,
  setDateRangeStart,
  dateRangeEnd,
  setDateRangeEnd,
  availableDates = [],
  compact = false,
  showLegend = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempSelection, setTempSelection] = useState({
    start: null,
    end: null,
    isSelecting: false,
  });
  const calendarRef = useRef(null);

  // Helper to parse 'YYYY-MM-DD' as a local date
  function parseLocalDate(dateStr) {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  // Helper to format a Date object as 'YYYY-MM-DD' in local time
  function formatDateLocal(date) {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Convert available dates to Set for O(1) lookup
  const availableDatesSet = React.useMemo(() => {
    return new Set(availableDates);
  }, [availableDates]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsOpen(false);
        setTempSelection({ start: null, end: null, isSelecting: false });
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Initialize temp selection when opening
  useEffect(() => {
    if (isOpen) {
      if (dateFilterType === "range" && dateRangeStart && dateRangeEnd) {
        setTempSelection({
          start: parseLocalDate(dateRangeStart),
          end: parseLocalDate(dateRangeEnd),
          isSelecting: false,
        });
      } else if (dateFilterType === "single" && singleDate) {
        const date = parseLocalDate(singleDate);
        setTempSelection({
          start: date,
          end: date,
          isSelecting: false,
        });
      } else {
        setTempSelection({ start: null, end: null, isSelecting: false });
      }
    }
  }, [isOpen, dateFilterType, dateRangeStart, dateRangeEnd, singleDate]);

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Handle date click
  const handleDateClick = (date) => {
    if (!tempSelection.isSelecting && !tempSelection.start) {
      // First click - start selection
      setTempSelection({
        start: date,
        end: null,
        isSelecting: true,
      });
    } else if (
      tempSelection.isSelecting &&
      tempSelection.start &&
      !tempSelection.end
    ) {
      // Second click - complete range selection
      const start = tempSelection.start;
      const end = date;

      if (start.getTime() === end.getTime()) {
        // Same date clicked - single date selection
        setTempSelection({
          start: date,
          end: date,
          isSelecting: false,
        });
      } else {
        // Range selection - ensure start is before end
        const sortedStart = start <= end ? start : end;
        const sortedEnd = start <= end ? end : start;

        setTempSelection({
          start: sortedStart,
          end: sortedEnd,
          isSelecting: false,
        });
      }
    } else {
      // Reset and start new selection
      setTempSelection({
        start: date,
        end: null,
        isSelecting: true,
      });
    }
  };

  // Handle OK button click - apply the selection
  const handleOk = () => {
    if (tempSelection.start) {
      const startDate = formatDateLocal(tempSelection.start);
      const endDate = tempSelection.end
        ? formatDateLocal(tempSelection.end)
        : startDate;

      if (startDate === endDate) {
        // Single date selection
        setSingleDate(startDate);
        setDateFilterType("single");
        setDateRangeStart("");
        setDateRangeEnd("");
      } else {
        // Range selection
        setDateRangeStart(startDate);
        setDateRangeEnd(endDate);
        setDateFilterType("range");
        setSingleDate("");
      }
    }
    setIsOpen(false);
    setTempSelection({ start: null, end: null, isSelecting: false });
  };

  // Handle Cancel button click
  const handleCancel = () => {
    setIsOpen(false);
    setTempSelection({ start: null, end: null, isSelecting: false });
  };

  // Clear filters
  const clearFilters = () => {
    setDateFilterType("all");
    setSingleDate("");
    setDateRangeStart("");
    setDateRangeEnd("");
    setIsOpen(false);
    setTempSelection({ start: null, end: null, isSelecting: false });
  };

  // Quick date range handlers - apply immediately and close calendar
  const setQuickRange = (days) => {
    const dateRange = getDefaultDateRange(days);
    setDateRangeStart(dateRange.start);
    setDateRangeEnd(dateRange.end);
    setDateFilterType("range");
    setSingleDate("");
    setIsOpen(false);
    setTempSelection({ start: null, end: null, isSelecting: false });
  };

  // Handler for yesterday quick select
  const setYesterday = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const yesterdayStr = formatDateLocal(yesterday);
    setSingleDate(yesterdayStr);
    setDateFilterType("single");
    setDateRangeStart("");
    setDateRangeEnd("");
    setIsOpen(false);
    setTempSelection({ start: null, end: null, isSelecting: false });
  };

  // Handler for All Time
  const setAllTime = () => {
    setDateFilterType("all");
    setSingleDate("");
    setDateRangeStart("");
    setDateRangeEnd("");
    setIsOpen(false);
    setTempSelection({ start: null, end: null, isSelecting: false });
  };

  // Check if yesterday is currently selected
  const isYesterdayActive = () => {
    if (dateFilterType !== "single" || !singleDate) return false;
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const expectedYesterday = formatDateLocal(yesterday);
    return singleDate === expectedYesterday;
  };

  // Check if a quick range is currently selected
  const isQuickRangeActive = (days) => {
    if (dateFilterType !== "range" || !dateRangeStart || !dateRangeEnd)
      return false;
    return isPastDaysRange(dateRangeStart, dateRangeEnd, days);
  };

  // Get current filter display text
  const getFilterText = () => {
    if (dateFilterType === "all") return "All Time";

    if (dateFilterType === "range" && dateRangeStart && dateRangeEnd) {
      if (isQuickRangeActive(7)) return "Past 7 Days";
      if (isQuickRangeActive(30)) return "Past 30 Days";
      if (isQuickRangeActive(90)) return "Past 90 Days";

      const startDate = parseLocalDate(dateRangeStart);
      const endDate = parseLocalDate(dateRangeEnd);

      const sameYear = startDate.getFullYear() === endDate.getFullYear();
      const currentYear = new Date().getFullYear();
      const showYear = !sameYear || startDate.getFullYear() !== currentYear;

      if (sameYear && !showYear) {
        return `${startDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })} - ${endDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })}`;
      } else {
        return `${startDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: showYear ? "numeric" : undefined,
        })} - ${endDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;
      }
    }

    if (singleDate) {
      const selectedDate = parseLocalDate(singleDate);
      const currentYear = new Date().getFullYear();
      const showYear = selectedDate.getFullYear() !== currentYear;

      return selectedDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: showYear ? "numeric" : undefined,
      });
    }

    return "Select Date Range";
  };

  // Get temp selection display text
  const getTempSelectionText = () => {
    if (!tempSelection.start) return "No selection";

    if (tempSelection.isSelecting && !tempSelection.end) {
      return `Start: ${tempSelection.start.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })} (click end date)`;
    }

    if (tempSelection.start && tempSelection.end) {
      if (
        formatDateLocal(tempSelection.start) ===
        formatDateLocal(tempSelection.end)
      ) {
        return `Selected: ${tempSelection.start.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;
      } else {
        return `Range: ${tempSelection.start.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })} - ${tempSelection.end.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })}`;
      }
    }

    return `Selected: ${tempSelection.start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  };

  // Check if date is in range
  const isDateInRange = (date) => {
    if (!tempSelection.start) return false;

    if (tempSelection.start && tempSelection.end) {
      return date >= tempSelection.start && date <= tempSelection.end;
    }

    return formatDateLocal(date) === formatDateLocal(tempSelection.start);
  };

  // Check if date is start or end of range
  const isDateRangeEnd = (date) => {
    return (
      (tempSelection.start &&
        formatDateLocal(date) === formatDateLocal(tempSelection.start)) ||
      (tempSelection.end &&
        formatDateLocal(date) === formatDateLocal(tempSelection.end))
    );
  };

  const calendarDays = generateCalendarDays();
  const today = new Date();

  return (
    <div className="relative w-full sm:w-auto" ref={calendarRef}>
      {/* Compact Button */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full sm:w-auto space-x-2 ${
          compact ? "px-3 py-2 rounded-xl" : "px-6 py-3 rounded-2xl"
        } bg-gray-700/60 backdrop-blur-xl border border-gray-600/50 hover:bg-[#B8460E]/20 hover:border-[#B8460E]/50 transition-all duration-300 text-sm cursor-pointer shadow-xl group relative overflow-hidden`}
      >
        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>

        <Calendar className="h-4 w-4 text-gray-300 group-hover:text-[#B8460E] transition-colors duration-300 relative z-10" />
        <span className="text-white font-medium relative z-10 truncate">
          {getFilterText()}
        </span>
        {dateFilterType !== "all" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              clearFilters();
            }}
            className="ml-1 p-0.5 hover:bg-white/20 rounded relative z-10"
          >
            <X className="h-3 w-3 text-gray-300" />
          </button>
        )}
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-300 group-hover:text-[#B8460E] transition-colors duration-300 relative z-10" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-300 group-hover:text-[#B8460E] transition-colors duration-300 relative z-10" />
        )}
      </div>

      {/* Calendar Modal */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-96 max-w-[90vw] rounded-2xl bg-gray-800/90 backdrop-blur-xl border border-gray-700 shadow-2xl z-50 p-4">
          {/* Glass morphism background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl"></div>

          {/* Content */}
          <div className="relative z-10">
            {/* Quick options */}
            <div className="mb-4">
              <div className="text-xs text-gray-300 mb-2 font-medium">
                Quick Select
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={setAllTime}
                  className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                    dateFilterType === "all"
                      ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                      : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                  }`}
                >
                  All Time
                </button>
                <button
                  onClick={setYesterday}
                  className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                    isYesterdayActive()
                      ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                      : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                  }`}
                >
                  Yesterday
                </button>
                <button
                  onClick={() => setQuickRange(7)}
                  className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                    isQuickRangeActive(7)
                      ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                      : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                  }`}
                >
                  Past 7 Days
                </button>
                <button
                  onClick={() => setQuickRange(30)}
                  className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                    isQuickRangeActive(30)
                      ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                      : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                  }`}
                >
                  Past 30 Days
                </button>
                <button
                  onClick={() => setQuickRange(90)}
                  className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                    isQuickRangeActive(90)
                      ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                      : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                  }`}
                >
                  Past 90 Days
                </button>
              </div>
            </div>

            {/* Calendar */}
            <div className="mb-4">
              <div className="text-xs text-gray-300 mb-2 font-medium">
                Select Date Range
              </div>

              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() =>
                    setCurrentMonth(
                      new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth() - 1
                      )
                    )
                  }
                  className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
                >
                  <ChevronDown className="h-4 w-4 text-gray-300 rotate-90" />
                </button>
                <h3 className="text-sm font-medium text-white">
                  {currentMonth.toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}
                </h3>
                <button
                  onClick={() =>
                    setCurrentMonth(
                      new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth() + 1
                      )
                    )
                  }
                  className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
                >
                  <ChevronDown className="h-4 w-4 text-gray-300 -rotate-90" />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Day headers */}
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                  <div
                    key={day}
                    className="text-xs text-gray-400 text-center py-2 font-medium"
                  >
                    {day}
                  </div>
                ))}

                {/* Calendar days */}
                {calendarDays.map((date, index) => {
                  const dateStr = formatDateLocal(date);
                  const hasData = availableDatesSet.has(dateStr);
                  const isCurrentMonth =
                    date.getMonth() === currentMonth.getMonth();
                  const isToday =
                    formatDateLocal(date) === formatDateLocal(today);
                  const isSelected = isDateInRange(date);
                  const isRangeEnd = isDateRangeEnd(date);

                  return (
                    <button
                      key={index}
                      onClick={() => handleDateClick(date)}
                      className={`
                        relative w-8 h-8 p-2 text-sm leading-[16px] rounded-[12px] transition-all duration-200 hover:bg-gray-600/50
                        ${!isCurrentMonth ? "text-gray-500" : "text-white"}
                        ${isSelected ? "bg-[#B8460E] text-white" : ""}
                        ${isRangeEnd ? "ring-2 ring-[#B8460E]/50" : ""}
                        ${isToday ? "font-bold" : ""}
                       
                      `}
                    >
                      {date.getDate()}
                      {hasData && !isSelected && (
                        <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-[#B8460E] rounded-full"></div>
                      )}
                      {hasData && isSelected && (
                        <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-[#fff] rounded-full"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selection info */}
            <div className="mb-4 p-3 bg-gray-700/50 rounded-xl backdrop-blur-sm">
              <div className="text-xs text-gray-300">
                {getTempSelectionText()}
              </div>
            </div>

            {/* Data summary */}
            {availableDates.length > 0 && (
              <div className="text-xs text-gray-300 mb-4 p-2 bg-gray-700/50 rounded-xl backdrop-blur-sm">
                📊 {availableDates.length} days with data available
              </div>
            )}

            {/* Legend */}
            {showLegend && availableDates.length > 0 && (
              <div className="mb-4 p-3 bg-gray-700/30 rounded-xl backdrop-blur-sm">
                <div className="text-xs text-gray-300 space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded bg-[#B8460E] flex items-center justify-center relative">
                      <span className="text-white text-xs">15</span>
                    </div>
                    <span>Selected date</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded border border-[#B8460E]/60 bg-[#B8460E]/15 flex items-center justify-center relative">
                      <span className="text-white text-xs">20</span>
                      <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#B8460E] rounded-full"></div>
                    </div>
                    <span>Has data</span>
                  </div>
                </div>
              </div>
            )}

            {/* OK and Cancel buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleOk}
                className="flex-1 px-4 py-2 text-sm bg-[#B8460E] text-white rounded-xl hover:bg-[#8B3A0E] transition-colors font-medium"
              >
                OK
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm bg-gray-600/50 text-gray-300 rounded-xl hover:bg-gray-500/50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDateRangePicker;
